/**
 * OMR Template Generator for React Native / Expo
 * -----------------------------------------------
 * Closest possible port of mcqgen/lib/omr/template.ts using
 * qrcode-generator instead of Node-only QRCode utilities.
 */

import qrcode from 'qrcode-generator';

const OMR_TEMPLATE_ID = 'mcqgen-omr-v1';
const OMR_MAX_QUESTION_COUNT = 100;

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function slugify(value: string) {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'omr-exam'
  );
}

function simpleHash(input: string, length: number): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return Math.abs(h).toString(36).slice(0, length).padStart(length, '0');
}

function compactCode(value: string, prefix: string, length: number) {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

  if (normalized.length > 0) {
    return `${prefix}${normalized.slice(0, length)}`;
  }

  return `${prefix}${simpleHash(value, length)}`;
}

function buildAnswerGrid(totalQuestions: number) {
  const questionsPerColumn = 20;
  const visibleColumns = Math.ceil(totalQuestions / questionsPerColumn);
  const columns: string[] = [];

  for (let column = 0; column < visibleColumns; column += 1) {
    const columnStart = column * questionsPerColumn + 1;
    const columnEnd = Math.min(columnStart + questionsPerColumn - 1, totalQuestions);
    const rows: string[] = [];

    rows.push(`
      <div class="head">
        <span></span><span>A</span><span>B</span><span>C</span><span>D</span>
      </div>
    `);

    for (let question = columnStart; question <= columnEnd; question += 1) {
      rows.push(`
        <div class="row">
          <span class="num">${question}</span>
          <span class="bubble" data-question="${question}" data-option="A">A</span>
          <span class="bubble" data-question="${question}" data-option="B">B</span>
          <span class="bubble" data-question="${question}" data-option="C">C</span>
          <span class="bubble" data-question="${question}" data-option="D">D</span>
        </div>
      `);
    }

    columns.push(`<div class="column">${rows.join('')}</div>`);
  }

  return columns.join('');
}

type QrPayload = {
  v: 'omr-qr-v1';
  sid: string;
  qc: number;
  rn: string;
  sig: string;
};

type QrDisplayData = {
  sheetId: string;
  studentName: string;
  rollNumber: string;
  subject: string;
  examId: string;
  questionCount: number;
  testDate: string;
  issuedAt: string;
};

function generateQrSvg(data: string, size = 260) {
  const qr = qrcode(0, 'M');
  qr.addData(data);
  qr.make();

  const moduleCount = qr.getModuleCount();
  const margin = 4;
  const totalModules = moduleCount + margin * 2;
  const moduleSize = size / totalModules;

  let rects = '';
  for (let row = 0; row < moduleCount; row += 1) {
    for (let col = 0; col < moduleCount; col += 1) {
      if (qr.isDark(row, col)) {
        const x = (col + margin) * moduleSize;
        const y = (row + margin) * moduleSize;
        rects += `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${moduleSize.toFixed(2)}" height="${moduleSize.toFixed(2)}" fill="#111111"/>`;
      }
    }
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">
    <rect width="${size}" height="${size}" fill="#FFFFFF"/>
    ${rects}
  </svg>`;
}

function createQrMarkup(payload: QrPayload, display: QrDisplayData) {
  const qrSvg = generateQrSvg(JSON.stringify(payload), 260);

  return `
    <section class="qr-section">
      <div class="qr-card">
        <div class="qr-code-wrap">
          <div class="qr-svg" aria-label="OMR QR code">
            ${qrSvg}
          </div>
        </div>
        <div class="qr-copy">
          <p class="qr-label">Sheet QR</p>
          <p class="qr-title">Scan to verify this OMR sheet</p>
          <div class="qr-meta">
            <div><span>Sheet ID</span><strong>${escapeHtml(display.sheetId)}</strong></div>
            <div><span>Student</span><strong>${escapeHtml(display.studentName)}</strong></div>
            <div><span>Roll no.</span><strong>${escapeHtml(display.rollNumber)}</strong></div>
            <div><span>Subject</span><strong>${escapeHtml(display.subject)}</strong></div>
            <div><span>Questions</span><strong>${display.questionCount}</strong></div>
            <div><span>Date</span><strong>${escapeHtml(display.testDate)}</strong></div>
          </div>
          <p class="qr-signature">Issued ${escapeHtml(display.issuedAt)} · SIG ${escapeHtml(payload.sig)}</p>
        </div>
      </div>
    </section>
  `;
}

function buildSheet(input: {
  studentId: string;
  studentName: string;
  rollNumber: string;
  subject: string;
  dateLabel: string;
  testDate: string;
  issuedAt: string;
  totalQuestions: number;
  examId: string;
}) {
  const sheetId = compactCode(`${input.examId}-${input.studentId}`, 's', 6);
  const payload: QrPayload = {
    v: 'omr-qr-v1',
    sid: sheetId,
    qc: input.totalQuestions,
    rn: input.rollNumber.trim(),
    sig: simpleHash(`${sheetId}:${input.rollNumber}:${input.totalQuestions}`, 6),
  };

  const qrMarkup = createQrMarkup(payload, {
    sheetId,
    studentName: input.studentName,
    rollNumber: input.rollNumber,
    subject: input.subject,
    examId: input.examId,
    questionCount: input.totalQuestions,
    testDate: input.testDate,
    issuedAt: input.issuedAt,
  });

  return `
    <div class="sheet">
      <div class="anchor top-left"></div>
      <div class="anchor top-right"></div>
      <div class="anchor bottom-right"></div>
      <div class="anchor bottom-left"></div>
      <div class="template-id">TEMPLATE: ${OMR_TEMPLATE_ID.toUpperCase()}</div>

      <section class="header">
        <h1 style="margin:0;font-size:22px;">MCQGEN OMR ANSWER SHEET</h1>
        <div class="meta">
          <div>${escapeHtml(input.studentName)}</div>
          <div>${escapeHtml(input.rollNumber)}</div>
          <div>${escapeHtml(input.subject)}</div>
          <div>${escapeHtml(input.dateLabel)}</div>
        </div>
      </section>

      <section class="answer-section">
        ${buildAnswerGrid(input.totalQuestions)}
      </section>

      ${qrMarkup}

      <div class="footer">
        <span>Fill one bubble only with dark black or blue pen.</span>
        <span>Do not fold, tear, or add stray marks.</span>
      </div>
    </div>
  `;
}

export type OmrStudent = {
  studentId: string;
  studentName: string;
  rollNumber: string;
};

export function buildOmrDocument(input: {
  subject: string;
  dateLabel: string;
  testDate: string;
  issuedAt: string;
  examId?: string;
  totalQuestions: number;
  students: OmrStudent[];
}) {
  const examId = input.examId ?? slugify(`${input.subject}-${input.testDate}`);
  const totalQuestions = Math.max(1, Math.min(input.totalQuestions, OMR_MAX_QUESTION_COUNT));

  const sheets = input.students.map((student) =>
    buildSheet({
      studentId: student.studentId,
      studentName: student.studentName,
      rollNumber: student.rollNumber,
      subject: input.subject,
      dateLabel: input.dateLabel,
      testDate: input.testDate,
      issuedAt: input.issuedAt,
      totalQuestions,
      examId,
    }),
  );

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>MCQGEN OMR Template v1</title>
  <style>
    @page { size: A4; margin: 0; }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    :root {
      --page-w: 210mm;
      --page-h: 297mm;
      --sheet-pad: 8mm;
      --anchor-size: 8mm;
      --answer-gap: 2.5mm;
      --col-width: 33.9mm;
      --row-num: 7mm;
      --bubble-track: 5.5mm;
      --bubble-size: 5.1mm;
    }
    body {
      margin: 0;
      padding: 0;
      background: #ece8df;
      font-family: Arial, sans-serif;
    }
    .sheet {
      width: var(--page-w);
      height: var(--page-h);
      margin: 0;
      padding: var(--sheet-pad);
      background: white;
      border: 2px solid #000;
      box-sizing: border-box;
      position: relative;
      overflow: hidden;
      break-after: page;
      page-break-after: always;
    }
    .sheet:last-child {
      break-after: auto;
      page-break-after: auto;
    }
    .anchor {
      width: var(--anchor-size);
      height: var(--anchor-size);
      background: #000;
      position: absolute;
    }
    .top-left { top: 4mm; left: 4mm; }
    .top-right { top: 4mm; right: 4mm; }
    .bottom-right { bottom: 4mm; right: 4mm; }
    .bottom-left { bottom: 4mm; left: 4mm; }
    .header {
      border: 1px solid #000;
      padding: 6mm;
      margin-top: 8mm;
      text-align: center;
    }
    .meta {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 3mm;
      margin-top: 4mm;
      font-size: 10px;
    }
    .meta div {
      border-bottom: 1px solid #000;
      padding-bottom: 2mm;
    }
    .template-id {
      position: absolute;
      top: 14mm;
      right: 18mm;
      font-size: 8px;
      font-weight: bold;
      letter-spacing: 0.08em;
      background: #fff;
      padding: 0 1mm;
    }
    .answer-section {
      border: 1px solid #000;
      margin-top: 10mm;
      padding: 4.5mm 3.5mm;
      display: grid;
      grid-template-columns: repeat(5, var(--col-width));
      column-gap: var(--answer-gap);
      justify-content: center;
      align-content: start;
      box-sizing: border-box;
    }
    .column {
      width: var(--col-width);
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1.7mm;
    }
    .head, .row {
      display: grid;
      grid-template-columns: var(--row-num) repeat(4, var(--bubble-track));
      align-items: center;
      justify-content: space-between;
      column-gap: 1.3mm;
      font-size: 10px;
      width: 100%;
    }
    .head {
      font-weight: 700;
      text-align: center;
      margin-bottom: 2mm;
    }
    .num {
      text-align: right;
      padding-right: 1mm;
      font-weight: 700;
    }
    .bubble {
      width: var(--bubble-size);
      height: var(--bubble-size);
      border-radius: 50%;
      border: 1px solid #000;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 8px;
      margin: 0 auto;
      user-select: none;
    }
    .bubble.filled {
      background: #111;
      color: transparent;
    }
    .qr-section {
      margin-top: 5mm;
      border: 1px solid #000;
      background: #faf8f2;
      padding: 5mm;
      min-height: 48mm;
      box-sizing: border-box;
    }
    .qr-card {
      display: grid;
      grid-template-columns: 48mm 1fr;
      gap: 6mm;
      align-items: center;
    }
    .qr-code-wrap {
      width: 46mm;
      height: 46mm;
      background: #fff;
      border: 1px solid #000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4mm;
      box-sizing: border-box;
    }
    .qr-svg {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .qr-svg svg {
      width: 100%;
      height: 100%;
      display: block;
    }
    .qr-copy {
      display: flex;
      flex-direction: column;
      gap: 1.8mm;
      min-width: 0;
    }
    .qr-label {
      margin: 0;
      font-size: 8px;
      font-weight: 700;
      letter-spacing: 0.16em;
      color: #0f766e;
      text-transform: uppercase;
    }
    .qr-title {
      margin: 0;
      font-size: 12px;
      font-weight: 700;
      color: #111827;
    }
    .qr-meta {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 2.4mm 3.4mm;
      font-size: 8px;
    }
    .qr-meta div {
      border: 1px solid #d1d5db;
      background: #fff;
      padding: 1.6mm 2mm;
      display: flex;
      flex-direction: column;
      gap: 0.8mm;
    }
    .qr-meta span {
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-size: 7px;
    }
    .qr-meta strong {
      font-size: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .qr-signature {
      margin: 0;
      font-size: 7px;
      letter-spacing: 0.08em;
      color: #475569;
    }
    .footer {
      margin-top: 4mm;
      display: flex;
      justify-content: space-between;
      font-size: 9px;
    }
    @media print {
      body { padding: 0; background: #fff; }
      .sheet { margin: 0; }
    }
  </style>
</head>
<body>
  ${sheets.join('')}
</body>
</html>
  `.trim();
}
